import { useEffect, useMemo, useState } from 'react';
import { useColorScheme } from '@mui/material';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import { decodeJWT } from 'aws-amplify/auth';

import { useAuthContext } from 'src/context/AuthContext';
import AuthUtils from 'src/utils/AuthUtils';
import { useSettingsContext } from 'src/components/settings';
import EncryptUtils from 'src/utils/EncryptUtils';
import { getBannerDetails, getModuleListData, getAdminPermissions } from 'src/services/service-handlers/PrivateService';
import { useBackgroundImages } from 'src/hooks/use-background-images';
import { SplashScreen } from 'src/components/loading-screen';
import { useBannerDetails } from 'src/context/BannerContext';
import i18next from 'i18next';
import { useTranslate } from 'src/locales';

import { authRoutes } from './auth';
import { mainRoutes } from './main';
import { getUserRoutes } from './user';
import { getAdminRoutes } from './admin';
import { useHasPermission } from 'src/utils/helper';
import { usePermissions } from 'src/context/PermissionsContext';

// ----------------------------------------------------------------------

export function PublicRouter() {
  return useRoutes([
    ...authRoutes,
    ...mainRoutes,
    { path: '/', element: <Navigate to="/sign-in" replace /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// ----------------------------------------------------------------------

export function PrivateRouter() {
  const location = useLocation();
  const settings = useSettingsContext();
  const { setBannerDetails } = useBannerDetails();
  const { mode, setMode } = useColorScheme();
  const { authState, isAdminPortal } = useAuthContext();
  const { isAdmin } = authState;
  const { i18n } = useTranslate('form');

  const token = AuthUtils.getAuthToken();

  const [decodedToken, setDecodedToken] = useState<any>(null);
  const [bannerData, setBannerData] = useState({});
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  const [moduleList, setModuleList] = useState([]);
  const [modulesFetched, setModulesFetched] = useState(false);
  const { bgImages, isLoading: bgImagesLoading } = useBackgroundImages();

  // Permission hooks
  const { permissions, setPermissions } = usePermissions();
  const hasPermission = useHasPermission();

  // ---------------- Decode Token ----------------
  useEffect(() => {
    if (!token) return;

    const decode = async () => {
      try {
        const decoded = await decodeJWT(token);
        setDecodedToken(decoded);
      } catch (err) {
        console.error('Token decode failed:', err);
      }
    };

    decode();
  }, [token]);

  // ---------------- Prevent back navigation ----------------
  useEffect(() => {
    const preventBack = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', preventBack);
    return () => window.removeEventListener('popstate', preventBack);
  }, []);

  // ---------------- Determine Portal Type ----------------
  useEffect(() => {
    if (!decodedToken) return;

    // If token does NOT contain user object → ADMIN
    if (!decodedToken?.payload?.user) {
      isAdminPortal({ isAdmin: true });
    }

    // console.log(decodedToken, 'decodedToken');
    // For admin users, fetch permissions dynamically from database (not just from JWT)
    // This ensures role changes take effect immediately without waiting for token expiry
    if (!decodedToken?.payload?.user) {
      // Admin user - fetch permissions dynamically
      (async () => {
        try {
          const result = await getAdminPermissions();
          if (result?.permissions) {
            setPermissions(result.permissions);
          } else if (decodedToken?.payload?.permissions) {
            // Fallback to JWT permissions if API fails
            setPermissions(decodedToken?.payload?.permissions || []);
          }
        } catch (error) {
          console.error('Failed to fetch admin permissions:', error);
          // Fallback to JWT permissions on error
          if (decodedToken?.payload?.permissions) {
            setPermissions(decodedToken?.payload?.permissions || []);
          }
        }
      })();
    } else if (decodedToken?.payload?.permissions) {
      // Regular user - use JWT permissions
      setPermissions(decodedToken?.payload?.permissions || []);
    }

    setAdminCheckDone(true);
  }, [decodedToken, isAdminPortal, setPermissions]);

  // ---------------- Fetch Banner Info ----------------
  useEffect(() => {
    const personId = decodedToken?.payload?.user?.PersonId;
    if (!personId) return;

    (async () => {
      try {
        const payload = { PersonId: personId };
        const encryptedPayload = EncryptUtils.encryptedData(payload);
        const result = await getBannerDetails({ tk: encryptedPayload });

        setBannerData(result);
        setBannerDetails(result);
      } catch (err) {
        console.error('Banner fetch failed:', err);
      }
    })();
  }, [decodedToken, setBannerDetails]);

  // ---------------- Fetch Module List ---------------- 
  useEffect(() => {
    if (!decodedToken) return;

    (async () => {
      try {
        const role = decodedToken.payload?.user ? 'user' : 'admin';
        const modules = await getModuleListData('park-city', role);

        // Load translations from module list if they exist
        modules.forEach((section: any) => {
          if (section?.translations) {
            Object.keys(section.translations).forEach((lang) => {
              i18next.addResourceBundle(lang, 'form', section.translations[lang], true, true);
            });
          }
        });

        // Force i18next to reload resources to trigger re-renders
        i18next.reloadResources();

        setModuleList(modules);
        setModulesFetched(true);
      } catch (err) {
        console.error('Module list fetch failed:', err);
        setModulesFetched(false);
      }
    })();
  }, [decodedToken]);


  // ------------------------------------------------------
  // IMPORTANT: DO NOT BUILD ROUTES UNTIL PERMISSIONS LOADED
  // ------------------------------------------------------
  const permissionsLoaded =
    (Array.isArray(permissions) && (permissions.length > 0 || permissions.includes('*'))) ||
    !isAdmin;

  const ready = decodedToken && adminCheckDone && modulesFetched && permissionsLoaded;


  // ------------------------------------------------------
  // LOADING ROUTES (STATIC, so memoized once)
  // ------------------------------------------------------
  const loadingRoutes = useMemo(() => [{ path: '*', element: <SplashScreen /> }], []);

  // ------------------------------------------------------
  // BUILD ROUTES (MEMOIZED & STABLE)
  // ------------------------------------------------------
  const routes = useMemo(() => {
    if (!ready) return loadingRoutes;

    return [
      ...(isAdmin
        ? getAdminRoutes(
            location.pathname,
            decodedToken,
            settings,
            mode,
            setMode,
            bannerData,
            moduleList,
            hasPermission,
            bgImages,
            i18n.language
          )
        : getUserRoutes(
            location.pathname,
            decodedToken,
            settings,
            mode,
            setMode,
            bannerData,
            moduleList,
            hasPermission,
            bgImages,
            i18n.language
          )),
      ...mainRoutes,
      { path: '/', element: <Navigate to="/profile" replace /> },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ready,
    isAdmin,
    location.pathname,
    decodedToken,
    settings,
    mode,
    setMode,
    bannerData,
    moduleList,
    hasPermission,
    bgImages,
    loadingRoutes,
    i18n.language, // Add language to dependencies so routes rebuild when language changes
  ]);

  // ------------------------------------------------------
  return useRoutes(routes);
}
